import { WaveCloudAPI, WAVE_APP_ID } from "@vida/wave-core";
import { createWaveApiConfig, trackActivity } from "./waveSession";
import { downloadBlobText } from "./waveBlobDownload";
import {
  extractAIJobOutputText,
  parseAssistantDirective,
  type AssistantDirective,
} from "../utils/waveAssistantInline";
import { WAVE_AGENT_MODEL_ID } from "./waveAgentModel";

export { parseAssistantDirective, type AssistantDirective };

const TERMINAL = new Set(["COMPLETED", "FAILED", "CANCELLED", "TIMED_OUT"]);

async function resolveJobText(job: {
  providerUsageJson?: string | null;
  outputAssetKey?: string | null;
}): Promise<string> {
  const inline = extractAIJobOutputText(job.providerUsageJson);
  if (inline) return inline;
  const key = job.outputAssetKey?.trim();
  if (key) return downloadBlobText(key);
  throw new Error("Output assistente non disponibile.");
}

async function pollAIJob(jobId: string, timeoutMs = 120_000): Promise<string> {
  const cfg = createWaveApiConfig();
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const job = await WaveCloudAPI.getAIJob(cfg, jobId);
    if (TERMINAL.has(job.status)) {
      if (job.status !== "COMPLETED") {
        throw new Error(job.errorMessage ?? `Job ${job.status}`);
      }
      return resolveJobText(job);
    }
    await new Promise((r) => setTimeout(r, 1500));
  }
  throw new Error("Timeout assistente.");
}

export async function runWaveAssistantIntegrate(
  waveId: string,
  blockContent: string,
  userPrompt: string,
): Promise<string> {
  const cfg = createWaveApiConfig();
  const params = {
    bedrock: {
      systemText:
        "Sei l'assistente Wave. Integra il contenuto esistente del blocco con la richiesta dell'utente. " +
        "Rispondi SOLO con il testo finale del blocco in italiano, senza spiegazioni né markdown.",
      userText:
        `CONTENUTO ATTUALE DEL BLOCCO:\n${blockContent || "(vuoto)"}\n\n` +
        `RICHIESTA UTENTE:\n${userPrompt}`,
      maxTokens: 4096,
    },
    waveId,
  };
  const result = await WaveCloudAPI.submitAIJob(cfg, {
    kind: "BEDROCK_CONVERSE",
    provider: "BEDROCK",
    prompt: userPrompt,
    paramsJson: JSON.stringify(params),
    inputAssetKeys: [],
    appId: WAVE_APP_ID,
    modelId: WAVE_AGENT_MODEL_ID,
    feature: "wave.agent.integrate",
  });
  trackActivity("Assistente", `Integrazione blocco (job ${result.jobId.slice(0, 8)}…)`);
  return pollAIJob(result.jobId);
}

export async function applyAssistantDirectiveOnBlur(
  waveId: string,
  text: string,
  onApply: (integrated: string) => void,
  onBusy?: (busy: boolean) => void,
): Promise<boolean> {
  const parsed = parseAssistantDirective(text);
  if (!parsed) return false;

  onBusy?.(true);
  onApply(parsed.bodyWithoutDirective);

  try {
    const integrated = await runWaveAssistantIntegrate(
      waveId,
      parsed.bodyWithoutDirective,
      parsed.prompt,
    );
    onApply(integrated.trim());
    trackActivity("Assistente", "Blocco aggiornato con integrazione AI.");
    return true;
  } catch (e) {
    const msg = e instanceof Error ? e.message : "Assistente non disponibile";
    trackActivity("Assistente", msg);
    throw e;
  } finally {
    onBusy?.(false);
  }
}
